import React, { useState, useContext, useEffect } from 'react';
import { Switch, Checkbox } from "@progress/kendo-react-inputs";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { PageContainer, Content, Header } from "../Drawer/PageContainer";
import { MsalContext } from "@azure/msal-react";
import InnerLoader from "../Shared/Loader/InnerLoader";
import Loader from "../Shared/Loader/loader";
import { CheckForInternalUser } from '../Helpers/miscellaneous'
import ErrorMessage from '../Shared/Error/ErrorMessage'
import Scrollable from "../Shared/Edit/Scrollable";
import { OverlayLoader } from "../Shared/Loader/OverlayLoader"
import { updateCustomer } from "../../API/Payment/Payment";
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { changeOrganizationOwner, getOrganizationUsers, updateOrganization, validateOrganizationName } from '../../API/Organization/organization';
import { LanguagesMap, runtimeLanguageMap } from './LanguagesMap';
import { useTranslation } from "react-i18next";
import { postMessageToRuntime } from '../Helpers/Runtime/runtime';
import { updateAdvanceModeInProject } from '../../API/Project/project';
import { isLibraryOrganization } from '../Helpers/Organization/Organization';

// function to render the organization users in the dropdown list
const userItemRender = (li, itemProps) => {
    const user = itemProps;
    const displayName = user.dataItem.displayName || "";
    const itemChildren = (
        <div className="pb-2 pt-2" style={{ display: "flex", alignItems: "center" }}>
            <div className="mx-1 circle d-inline-block d-center" style={{ background: user.dataItem.bgColor, color: "#fff" }}>
                {user.dataItem.email && user.dataItem.email.substring(0, 2).toUpperCase()}
            </div>
            <div className="ps-2">
                <h5 className="mb-0">{displayName}</h5>
                <div className="text-muted">{user.dataItem.email} - <small className="font-size-0">{user.dataItem.roleType}</small></div>
            </div>
        </div>
    );
    return React.cloneElement(li, li.props, itemChildren);
};

// function to render the selected user in the dropdown list
const userValueRender = (li, itemProps) => {
    const user = itemProps;
    const displayName = user.displayName || "";
    const itemChildren = (
        <div>{user.email && user.email.length > 0 &&
            <div className="pb-2 pt-2" style={{ display: "flex", alignItems: "center" }}>
                <div className="mx-1 circle d-inline-block d-center" style={{ background: user.bgColor, color: "#fff" }}>
                    {user.email && user.email.substring(0, 2).toUpperCase()}
                </div>
                <div className="ps-2">
                    <h5 className='mb-0'>{displayName}</h5>
                    <div className="text-muted">{user.email} - <small className="font-size-0">{user.roleType}</small></div>
                </div>
            </div>
        }</div>
    );
    return React.cloneElement(li, li.props, itemChildren);
};

const Overview = (props) => {

    // msal context
    const context = useContext(MsalContext);

    // loading states
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [changingOwner, setChangingOwner] = useState(false);
    const [deletingOrganization, setDeletingOrganization] = useState(false);

    // props states
    const [organization, setOrganization] = useState(props.organization);
    const [userRole, setUserRole] = useState(props.userRole);

    const { t, i18n } = useTranslation([
        "organization/common",
        "organization/change_organization_dialog",
    ], {
        useSuspense: false,
    });

    // user states
    const [organizationUsers, setOrganizationUsers] = useState([]);
    const [owner, setOwner] = useState({});
    const [selectedOwner, setSelectedOwner] = useState({});

    // form states
    const [organizationName, setOrganizationName] = useState(organization.orgName);
    const [organizationUrl, setOrganizationUrl] = useState(organization.orgUrl);
    const [organizationDescription, setOrganizationDescription] = useState(organization.description);
    const [internalMode, setInternalMode] = useState(organization.internalMode);
    const [isAIEnabled, setIsAIEnabled] = useState(organization.isAIEnabled);
    const organizationLanguage = LanguagesMap.find((language) => language.code === organization.platformLanguage);
    const [platformLanguage, setPlatformLanguage] = useState(organizationLanguage || LanguagesMap[0]);
    const [languageSelectionOnAppCreation, setLanguageSelectionOnAppCreation] = useState(organization.languageSelectionOnAppCreation || false);
    const [isMfaRequired, setIsMfaRequired] = useState(organization.isMfaRequired || false);

    // date/time format settings
    const [dateFormat, setDateFormat] = useState(organization.timeSettings?.dateFormat || "MM/dd/yyyy");
    const [timeFormat, setTimeFormat] = useState(organization.timeSettings?.timeFormat || "h:mm a");
    const [startOfWeek, setStartOfWeek] = useState(organization.timeSettings?.startOfWeek || "Sunday");
    
    // fiscal settings
    const [useFiscalCalendar, setUseFiscalCalendar] = useState(organization.fiscalSettings?.useFiscalCalendar || false);
    const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(organization.fiscalSettings?.fiscalYearStartMonth || "January");
    const [fiscalYearLabelFormat, setFiscalYearLabelFormat] = useState(organization.fiscalSettings?.fiscalYearLabelFormat || "FY + Ending Year");

    // date/time format options
    const dateFormatOptions = [
        { value: "MM/dd/yyyy", label: "MM/DD/YYYY → 10/15/2025" },
        { value: "dd/MM/yyyy", label: "DD/MM/YYYY → 15/10/2025" },
        { value: "yyyy-MM-dd", label: "YYYY-MM-DD → 2025-10-15" },
        { value: "MMM d, yyyy", label: "MMM D, YYYY → Oct 15, 2025" },
        { value: "MMMM d, yyyy", label: "MMMM D, YYYY → October 15, 2025" },
        { value: "d MMM yyyy", label: "D MMM YYYY → 15 Oct 2025" },
        { value: "d MMMM yyyy", label: "D MMMM YYYY → 15 October 2025" },
        { value: "EEE, MMM d, yyyy", label: "EEE, MMM D, YYYY → Wed, Oct 15, 2025" },
        { value: "EEEE, MMMM d, yyyy", label: "EEEE, MMMM D, YYYY → Wednesday, October 15, 2025" },
        { value: "EEE, d MMM yyyy", label: "EEE, D MMM YYYY → Wed, 15 Oct 2025" },
        { value: "EEEE, d MMMM yyyy", label: "EEEE, D MMMM YYYY → Wednesday, 15 October 2025" }
    ];

    const timeFormatOptions = [
        { value: "h:mm a", label: "H:MM A → 3:45 PM" },
        { value: "HH:mm", label: "HH:MM → 15:45" },
        { value: "h:mm a z", label: "H:MM A Z → 3:45 PM EDT" },
        { value: "HH:mm z", label: "HH:MM Z → 15:45 EDT" }
    ];

    const startOfWeekOptions = [
        { value: "Sunday", label: "Sunday" },
        { value: "Monday", label: "Monday" }
    ];

    const fiscalYearStartMonthOptions = [
        { value: "January", label: "January" },
        { value: "February", label: "February" },
        { value: "March", label: "March" },
        { value: "April", label: "April" },
        { value: "May", label: "May" },
        { value: "June", label: "June" },
        { value: "July", label: "July" },
        { value: "August", label: "August" },
        { value: "September", label: "September" },
        { value: "October", label: "October" },
        { value: "November", label: "November" },
        { value: "December", label: "December" }
    ];

    const fiscalYearLabelFormatOptions = [
        { value: "FY + Ending Year", label: "FY + Ending Year (e.g., FY 2025)" },
        { value: "FY + Starting Year", label: "FY + Starting Year (e.g., FY 2024)" },
        { value: "FY [StartYearShort]-[EndYearShort]", label: "FY [StartYearShort]-[EndYearShort] (e.g., FY 24-25)" }
    ];

    // dialog states
    const [showChangeOrganizationDialog, setShowChangeOrganizationDialog] = useState(false);
    //const [showDeleteOrganizationDialog, setShowDeleteOrganizationDialog] = useState(false);
    const [updatedOrganizationName, setUpdatedOrganizationName] = useState("");
    const [consentAccepted, setConsentAccepted] = useState(false);
    //const [deleteOrganizationName, setDeleteOrganizationName] = useState("");

    //error states
    const [error, setError] = useState({ isValid: true, message: "" });

    useEffect(() => {
        // Define an asynchronous function that fetches the necessary data
        const getOrganizationUsersData = async () => {
            // try catch block to catch any errors
            try {
                // get organization users and owner
                const { owner, organizationUsers } = await getOwnerAndOtherUsers();
                setOrganizationUsers(organizationUsers); // set organization users
                setOwner(owner); // set owner
            } catch (error) {
                console.log(error); // log error
            } finally {
                setLoading(false); // set loading to false
            }
        }
        getOrganizationUsersData();
    }, [props.organization]);

    // function to get organization users
    const getOwnerAndOtherUsers = async () => {
        const results = await getOrganizationUsers(organization._id);
        const organizationUsers = results.filter((user) => user.invitationAccepted === "Accepted" && user.isSubscriptionUser);
        const owner = organizationUsers.find((user) => user.roleType === "Owner");
        return { owner, organizationUsers };
    };

    // handle the change of the organization name
    const handleOrganizationNameChange = (e) => {
        let { value } = e.target;
        value = value.replace(/\s/g, "").trim();
        let specialCharactersEntered = /[^a-zA-Z0-9\-\.\_\—]/g.test(value);

        if (value.trim() === "") {
            setError({ isValid: false, message: t("organization_settings.errors.name_required") });
        } else if (value.trim().match(/^\d/)) {
            setError({ isValid: false, message: t("organization_settings.errors.name_starts_with_number") });
        } else if (specialCharactersEntered) {
            setError({ isValid: false, message: t("organization_settings.errors.name_contain_specical_character") });
        } else {
            setError({ isValid: true, message: "" });
        }

        setOrganizationName(value);
        setOrganizationUrl(value);
    }

    // function to disable the save button if there are no changes made
    const shouldSaveButtonDisabled = () => {
        const defaultOrganizationLanguageSelectionOnAppCreation = organization.languageSelectionOnAppCreation || false;
        const condition =
            organization.description !== organizationDescription ||
            organization.orgName.trim() !== organizationName.trim() ||
            organization.orgUrl.trim() !== organizationUrl.trim() ||
            organization.internalMode !== internalMode ||
            organization.isAIEnabled !== isAIEnabled ||
            organization.isMfaRequired !== isMfaRequired ||
            defaultOrganizationLanguageSelectionOnAppCreation !== languageSelectionOnAppCreation ||
            organization.platformLanguage !== platformLanguage.code ||
            organization.timeSettings?.dateFormat !== dateFormat ||
            organization.timeSettings?.timeFormat !== timeFormat ||
            organization.timeSettings?.startOfWeek !== startOfWeek ||
            organization.fiscalSettings?.useFiscalCalendar !== useFiscalCalendar ||
            organization.fiscalSettings?.fiscalYearStartMonth !== fiscalYearStartMonth ||
            organization.fiscalSettings?.fiscalYearLabelFormat !== fiscalYearLabelFormat;

        if (organizationName.trim() === "" || !error.isValid) return true;

        return !condition;
    };

    // function to update the organization data
    const onUpdateOrganization = async (e) => {
        e.preventDefault(); // prevent the default action of the form
        try {
            setUpdating(true); // set updating to true

            let isOrganizationNameTaken = organization.orgName.trim() === organizationName.trim() ? false : await checkIfOrganizationNameAvailable();  // check if organization name is available

            if (isOrganizationNameTaken) {
                setError({ isValid: false, message: t("organization_settings.errors.name_already_exists") });
                return;
            }

            if (organization.orgName.trim() !== organizationName.trim()) {
                setShowChangeOrganizationDialog(true); // show change organization dialog
                setUpdatedOrganizationName(""); // set updated organization name
                return;
            }
            let properties = {
                _id: organization._id,
                description: organizationDescription || "",
                internalMode: internalMode || false,
                isAIEnabled: isAIEnabled || false,
                isMfaRequired: isMfaRequired || false,
                languageSelectionOnAppCreation: languageSelectionOnAppCreation || false,
                platformLanguage: platformLanguage.code || "en",
                timeSettings: {
                    dateFormat: dateFormat || "MM/dd/yyyy",
                    timeFormat: timeFormat || "h:mm a",
                    timeZone: "UTC",
                    startOfWeek: startOfWeek || "Sunday"
                },
                fiscalSettings: {
                    useFiscalCalendar: useFiscalCalendar || false,
                    fiscalYearStartMonth: fiscalYearStartMonth || "January",
                    fiscalYearLabelFormat: fiscalYearLabelFormat || "FY + Ending Year"
                }
            };
            await updateOrganization(properties)

            const result = { ...organization, ...properties };

            if (!result.internalMode && organization.internalMode) {
                await updateAdvanceModeInProject(organization._id); // update all project to set advance mode to false
            }

            if (organization.platformLanguage !== properties.platformLanguage) {
                postMessageToRuntime({
                    eventType: "LANGUAGE_CHANGED",
                    value: runtimeLanguageMap.find((language) => language.code === properties.platformLanguage).runtimeCode || "en"
                })
                i18n.changeLanguage(properties.platformLanguage);
            }

            // Send date/time format settings to runtime
            postMessageToRuntime({
                eventType: "DATE_TIME_SETTINGS_CHANGED",
                value: {
                    timeSettings: properties.timeSettings,
                    fiscalSettings: properties.fiscalSettings
                }
            });

            setOrganizationName(result.orgName); // set organization name
            setOrganizationDescription(result.description); // set organization description
            setOrganizationUrl(result.orgUrl); // set organization url
            setInternalMode(result.internalMode); // set internal mode
            setOrganization(result); // set organization
            setPlatformLanguage(LanguagesMap.find((language) => language.code === result.platformLanguage)); // set platform language
        } catch (error) {
            console.log(error); // log error
        } finally {
            setUpdating(false); // set updating to false
        }
    };

    // function to check if organization name is available
    const checkIfOrganizationNameAvailable = async (e) => {
        let response = await validateOrganizationName(organizationName)
        if (organization.orgName.trim() === organizationName.trim()) {
            response = "notTaken"
        }
        return response === "taken" ? true : false;
    };

    // function to disable the change organizationo button if updated organization name does not match
    const enableChangeButton = () => {
        return (updatedOrganizationName.trim() !== organizationName.trim() || consentAccepted === false);
    };

    // funtion to save organization name
    const saveOrganizationName = async (e) => {
        e.preventDefault(); // prevent the default action of the form
        try {
            setUpdating(true); // set updating to true

            let propertiestoUpdateForDesigner = {
                _id: organization._id,
                description: organizationDescription || "",
                internalMode: internalMode || false,
                orgName: organizationName.trim() || "",
                orgUrl: organizationUrl.trim().replace(/\s/g, ""),
                isAIEnabled: isAIEnabled,
                isMfaRequired: isMfaRequired || false,
                languageSelectionOnAppCreation: languageSelectionOnAppCreation || false,
                platformLanguage: platformLanguage.code || "en",
                timeSettings: {
                    dateFormat: dateFormat || "MM/dd/yyyy",
                    timeFormat: timeFormat || "h:mm a",
                    timeZone: "UTC",
                    startOfWeek: startOfWeek || "Sunday"
                },
                fiscalSettings: {
                    useFiscalCalendar: useFiscalCalendar || false,
                    fiscalYearStartMonth: fiscalYearStartMonth || "January",
                    fiscalYearLabelFormat: fiscalYearLabelFormat || "FY + Ending Year"
                }
            }

            await updateOrganization(propertiestoUpdateForDesigner); // update organization

            const result = { ...organization, ...propertiestoUpdateForDesigner };

            if (!result.internalMode && organization.internalMode) {
                await updateAdvanceModeInProject(organization._id); // update all project to set advance mode to false
            }

            if (organization.platformLanguage !== propertiestoUpdateForDesigner.platformLanguage) {
                postMessageToRuntime({
                    eventType: "LANGUAGE_CHANGED",
                    value: runtimeLanguageMap.find((language) => language.code === propertiestoUpdateForDesigner.platformLanguage).runtimeCode || "en"
                })
                i18n.changeLanguage(propertiestoUpdateForDesigner.platformLanguage);
            }

            // Send date/time format settings to runtime
            postMessageToRuntime({
                eventType: "DATE_TIME_SETTINGS_CHANGED",
                value: {
                    timeSettings: propertiestoUpdateForDesigner.timeSettings,
                    fiscalSettings: propertiestoUpdateForDesigner.fiscalSettings
                }
            });

            setOrganizationName(result.orgName); // set organization name 
            setOrganizationDescription(result.description); // set organization description 
            setOrganizationUrl(result.orgUrl); // set organization url 
            setInternalMode(result.internalMode); // set internal mode 
            setOrganization(result); // set organization
            setPlatformLanguage(LanguagesMap.find((language) => language.code === result.platformLanguage)); // set platform language
            localStorage.setItem("selectedOrganization", result.orgName);
            if (props.showInIframe) {
                props.reload(result.orgUrl);
            } else {
                window.location.href = process.env.REACT_APP_DESIGNER_URL + "/" + result.orgName + "/_Organisation/organizationOverview";
            }
        } catch (error) {
            console.log(error); // log error   
        } finally {
            setUpdating(false); // set updating to false     
            setShowChangeOrganizationDialog(false); // hide change organization dialog
            setUpdatedOrganizationName(""); // set updated organization name
            setConsentAccepted(false); // set consent accepted to false
        }
    };

    // function to change the organization owner
    const changeOwner = async (e) => {
        e.preventDefault(); // prevent the default action of the form
        try {
            setChangingOwner(true);
            // update the customer name and email in stripe
            const firstName = selectedOwner.firstName || "";
            const lastName = selectedOwner.lastName || "";

            await updateCustomer(organization._id, {
                stripeCustomerId: organization.stripeCustomerId,
                name: firstName + " " + lastName,
                email: selectedOwner.email
            });

            // change the organization owner
            const result = await changeOrganizationOwner(organization._id, owner._id, selectedOwner._id);
            setUserRole(selectedOwner.roleType) // set the user role to the selected owners role
            setOwner(selectedOwner);  // set the owner to the selected owners
            if (!props.showInIframe) {
                window.location.reload();  // reload the page
            }
            setOrganization(result);  // set the organization to the result
        } catch (error) {   // catch any errors that may be thrown  
            console.log(error);  // log the error  
        } finally {
            setSelectedOwner({});  // reset the selected owner to empty
            setChangingOwner(false);   // reset the updating state to false  
        }
    }

    // function to disable the deete organizationo button if updated organization name does not match
    // const enableDeleteButton = () => {
    //     return (deleteOrganizationName.trim() !== organization.orgName.trim());
    // };

    //funtion to delete organization
    // const deleteOrganization = async (e) => {
    //     e.preventDefault();

    //     try {
    //         setDeletingOrganization(true);
    //         await enabledisableOnRuntime(context, "Organization", organization.orgName, organization._id, null, null, true);
    //         await updateOrganization({ _id: organization._id, isActive: false });
    //         if (props.showInIframe) {
    //             props.reload()
    //         } else {
    //             window.location.href = "/_CreateOrganization";
    //         }
    //     } catch (error) {
    //         console.error(error); // log error as an error instead of a log message 
    //     } finally {
    //         setDeletingOrganization(false);
    //         setShowDeleteOrganizationDialog(false);
    //         setDeleteOrganizationName("");
    //     }
    // }

    // set loading to true while the data is being fetched
    if (loading) return <Loader />

    // render the organization overview page
    return (
        <PageContainer>
            {
                !props.showInIframe &&
                <Header
                    header="Organization Details"
                >
                </Header>
            }
            <Content>
                <Scrollable>
                    {(updating || changingOwner || deletingOrganization) && <OverlayLoader />}
                    {showChangeOrganizationDialog &&
                        <Dialog
                            width={"42%"}
                            title={t("title", { ns: "organization/change_organization_dialog" })}
                            onClose={() => { setShowChangeOrganizationDialog(false); setUpdatedOrganizationName(""); setConsentAccepted(false); }}
                        >
                            <div className="dialog-content">
                                <div className="pb-3 form-label">
                                    {t("warning1", { ns: "organization/change_organization_dialog" })}
                                </div>
                                <div className="pb-2">
                                    {t("warning2", { ns: "organization/change_organization_dialog" })}
                                </div>
                                <div className="pb-2">
                                    {t("warning3", { ns: "organization/change_organization_dialog", url: process.env.REACT_APP_DESIGNER_URL + "/" + organization.orgName })}
                                </div>
                                <div className="pb-2">
                                    {t("warning4", { ns: "organization/change_organization_dialog" })}
                                </div>
                                <div className="row">
                                    <div className="col-6">
                                        <div className="form-label">
                                            {t("current_organization_name_field_label", { ns: "organization/change_organization_dialog" })}
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="form-label">
                                            {t("new_organization_name_field_label", { ns: "organization/change_organization_dialog" })}
                                        </div>
                                    </div>
                                </div>
                                <div className="row pb-2">
                                    <div className="col-6">
                                        <div className="disabled-input-wrapper">
                                            <input value={organization.orgName} disabled={true} className="form-control" />
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <input
                                            autoFocus={true}
                                            onChange={(e) => setUpdatedOrganizationName(e?.target?.value?.replace(/\s/g, "")?.trim() || "")}
                                            value={updatedOrganizationName}
                                            className="form-control"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Checkbox className="mt-2" checked={consentAccepted} onChange={(e) => setConsentAccepted(e.target.value)}
                                        label={t("terms_and_conditions_label", { ns: "organization/change_organization_dialog" })} />
                                </div>
                            </div>

                            <div className="mt-1">
                                <DialogActionsBar layout="end">
                                    {updating && (
                                        <small className="mt-2">
                                            <div className="d-inline-block">
                                                <InnerLoader borderColor="green" />
                                                <span className="me-4 text-danger"></span>
                                            </div>
                                        </small>
                                    )}
                                    <button
                                        onClick={() => {
                                            setShowChangeOrganizationDialog(false);
                                            setUpdatedOrganizationName("");
                                            setConsentAccepted(false);
                                        }}
                                        type={"button"}
                                        className="btn btn-secondary"
                                    >
                                        {t("buttons.cancel_button_label", { ns: "organization/change_organization_dialog" })}
                                    </button>
                                    <button type={"button"} className="btn btn-primary" onClick={saveOrganizationName} disabled={enableChangeButton()}>
                                        {t("buttons.change_button_label", { ns: "organization/change_organization_dialog" })}
                                    </button>
                                </DialogActionsBar>
                            </div>
                        </Dialog>
                    }
                    {/* {showDeleteOrganizationDialog &&
                        <Dialog width={"42%"} title="Delete Organization" onClose={() => { setShowDeleteOrganizationDialog(false); setDeleteOrganizationName("") }}>
                            <div className="l-120 dialog-content">
                                <div className="pb-4 form-label">Please read the following before you proceed:</div>
                                <p>
                                    All users in your organization will immediately lose access to the organization and all apps created under it.
                                </p>
                                <p>
                                    You will have up to 30 days to recover this organization, after which it will be permanently deleted.
                                </p>
                                <p >
                                    This will result in the loss of all organizations and related app artifacts.
                                </p>
                                <div>
                                    The existing organization URL:<span className="txt-link">
                                        <b> {process.env.REACT_APP_DESIGNER_URL + "/" + organization.orgName} </b>
                                    </span>
                                    will not be redirected.
                                </div>
                            </div>

                            <div className="pt-5 dialog-content">
                                To confirm this action, please type
                                <b className="text-primary"> {organization.orgName}</b>
                            </div>
                            <div>
                                <input
                                    autoFocus={true}
                                    onChange={(e) => setDeleteOrganizationName(e.target.value)}
                                    value={deleteOrganizationName}
                                    className="my-3 form-control w-50"
                                />
                            </div>
                            <div className="py-1 dialog-content"> By clicking delete, you agree to the
                                <a href="#" className="mx-1">Terms of Service</a> and
                                <a href="#" className="mx-1">Privacy Statement</a>.
                            </div>

                            <DialogActionsBar layout="end">
                                <div className="mb-3 me-3">
                                    {deletingOrganization && (
                                        <small className="mt-2">
                                            <div className="d-inline-block">
                                                <InnerLoader borderColor="red" />
                                                <span className="me-4 text-danger"></span>
                                            </div>
                                        </small>
                                    )}
                                    <button onClick={() => { setShowDeleteOrganizationDialog(false); setDeleteOrganizationName("") }} type={"button"} className="btn btn-secondary">
                                        Cancel
                                    </button>

                                    <button type={"button"} className={"btn btn-danger"} onClick={deleteOrganization} disabled={enableDeleteButton()}>
                                        Delete
                                    </button>
                                </div>
                            </DialogActionsBar>
                        </Dialog>
                    } */}
                    <div className="row">
                        {/* Left Column - Main Settings */}
                        <div className={`p-0 col-md-12 col-lg-6 mx-2 ${props.showInIframe ? 'checkRuntimeUser' : ''}`}>
                            <div className="mx-1">
                                <div className="form-label d-inline-block" required>
                                    {t("organization_settings.name_field_label")}
                                </div>
                                <ErrorMessage
                                    valid={error.isValid}
                                    error={error.message}
                                />
                                <div>
                                    <input
                                        maxLength="64"
                                        placeholder="Only letter, space, number is allowed"
                                        className="form-control name-text"
                                        onChange={handleOrganizationNameChange}
                                        value={organizationName}
                                    />
                                </div>
                                {
                                    CheckForInternalUser(props.user.email) &&
                                    <div className="mt-4 mb-2">
                                        <div className="form-label">
                                            {t("organization_settings.description_field_label")}
                                        </div>
                                        <textarea
                                            rows={4}
                                            className="form-control"
                                            onChange={(e) => setOrganizationDescription(e.target.value)}
                                            value={organizationDescription}
                                        />
                                    </div>
                                }
                                <div className="form-label d-inline-block" required>
                                    {t("organization_settings.url_field_label")}
                                </div>
                                <div className="py-1 fontUrl" style={{ wordWrap: "break-word" }}>
                                    {process.env.REACT_APP_RUNTIME_URL}/<small className="txt-link">{organization.orgUrl}</small>
                                </div>
                                <div className="disabled-input-wrapper">
                                    <input
                                        className="form-control name-text"
                                        disabled={true}
                                        value={organizationUrl}
                                    />
                                </div>
                                {
                                    CheckForInternalUser(props.user.email) &&
                                    <div className="mt-4">
                                        <div className="form-label">
                                            {t("organization_settings.internal_mode_field_label")}
                                        </div>
                                        <Switch
                                            name="internalMode"
                                            onChange={(e) => setInternalMode(e.target.value)}
                                            checked={internalMode}
                                        />
                                    </div>
                                }
                                {!organization.isTenantOrganization &&
                                    <div className="mt-4">
                                        <div className="form-label">
                                            {t("organization_settings.ai_enabled_field_label")}
                                        </div>
                                        <Switch
                                            name="isAIEnabled"
                                            onChange={(e) => setIsAIEnabled(e.target.value)}
                                            checked={isAIEnabled}
                                        />
                                    </div>
                                }
                                <div className="mt-4">
                                    <div className="form-label">
                                        {t("organization_settings.mfa_enabled_field_label")}
                                    </div>
                                    <Switch
                                        name="isMfaRequired"
                                        onChange={(e) => setIsMfaRequired(e.target.value)}
                                        checked={isMfaRequired}
                                    />
                                </div>
                                <div className="mt-4">
                                    <div className="form-label">
                                        {t("organization_settings.language_field_label")}
                                    </div>
                                    <DropDownList
                                        id="languageDropdown"
                                        data={LanguagesMap}
                                        textField="name"
                                        valueField="code"
                                        value={platformLanguage}
                                        onChange={(e) => {
                                            setPlatformLanguage(e.target.value);
                                        }}
                                    />
                                </div>
                                {!organization.isTenantOrganization && (
                                    <div className="mt-4">
                                        <div className="form-label">
                                            {t("organization_settings.language_selection_app_creation_field_label")}
                                        </div>
                                        <Switch
                                            name="languageSelectionOnAppCreation"
                                            onChange={(e) => setLanguageSelectionOnAppCreation(e.target.value)}
                                            checked={languageSelectionOnAppCreation}
                                        />
                                    </div>
                                )}
                                
                                <div className="row pt-4">
                                    <div className="col">
                                        <button
                                            type="button"
                                            className="mt-2 btn btn-primary"
                                            disabled={shouldSaveButtonDisabled()}
                                            onClick={onUpdateOrganization}
                                        >
                                            {t("organization_settings.buttons.save_button_label")}
                                        </button>
                                        {updating && (
                                            <div className="ms-2 d-inline-block">
                                                <InnerLoader borderColor="green" />
                                                <span className="text-danger"></span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="overview-divider"></div>

                                <div className="header">
                                    {t("change_owner_settings.title")}
                                </div>

                                <div className="pb-2 pt-2" style={{ display: "flex", alignItems: "center" }}>
                                    <div className="me-1 circle d-inline-block d-center" style={{ background: owner.bgColor, color: "#fff" }}>
                                        {owner.email && owner.email.substring(0, 2).toUpperCase()}
                                    </div>
                                    <div className="ps-2">
                                        <h5 className="mb-0">{owner.displayName}</h5>
                                        <div className="text-muted">{owner.email}</div>
                                    </div>
                                </div>

                                {(userRole === "Owner" && !isLibraryOrganization(organization.orgName)) && (
                                    <div>
                                        <div className="form-label d-inline-block me-1">
                                            {t("change_owner_settings.change_owner_field_label")}
                                        </div>
                                        <div className="row">
                                            <div className="col-12">
                                                <DropDownList
                                                    placeholder="Select User"
                                                    data={organizationUsers.filter((user) => user._id !== owner._id)}
                                                    className="form-control-custom show-x-button"
                                                    textField="email"
                                                    itemRender={userItemRender}
                                                    valueRender={userValueRender}
                                                    onChange={(e) => setSelectedOwner(e.target.value)}
                                                    value={selectedOwner}
                                                />
                                            </div>
                                            <div className="col-12 my-4">
                                                <button
                                                    type={"button"}
                                                    className="btn btn-primary me-3"
                                                    onClick={changeOwner}
                                                    disabled={selectedOwner.email === undefined || selectedOwner.email === owner.email}
                                                >
                                                    {t("change_owner_settings.buttons.change_owner_button_label")}
                                                </button>
                                                {changingOwner && (
                                                    <small className="mt-2">
                                                        <div className="d-inline-block">
                                                            <InnerLoader borderColor="green" />
                                                            <span className="ms-4 text-danger"></span>
                                                        </div>
                                                    </small>
                                                )}
                                            </div>
                                        </div>

                                        {/* <div className="overview-divider"></div>

                                        <div className="header pb-1">
                                            Delete Organization
                                        </div>

                                        <button
                                            type="button"
                                            disabled={isLibraryOrganization(organization.orgName)}
                                            className="mt-2 mb-2 btn btn-danger"
                                            onClick={() => setShowDeleteOrganizationDialog(true)}>
                                            <span className="mx-2">
                                                <i className="fa-solid fa-trash-can my-2 pe-2"></i>Delete Organization
                                            </span>
                                        </button>
                                        <div className="pb-4">
                                            <i className="fa-solid fa-triangle-exclamation mx-1" style={{ color: 'coral' }}></i>
                                            This will affect all apps and members of this organization.
                                        </div> */}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Right Column - Date/Time Settings */}
                        <div className="p-0 col-md-12 col-lg-5 mx-2">
                            <div className="mx-1">
                                {/* Date/Time Format Settings */}
                                <div className="header mt-4">
                                    {t("organization_settings.date_time_settings_title")}
                                </div>
                                
                                <div className="mt-4">
                                    <div className="form-label">
                                        {t("organization_settings.date_format_field_label")}
                                    </div>
                                    <DropDownList
                                        id="dateFormatDropdown"
                                        data={dateFormatOptions}
                                        textField="label"
                                        valueField="value"
                                        value={dateFormat}
                                        onChange={(e) => setDateFormat(e.target.value)}
                                    />
                                </div>
                                
                                <div className="mt-4">
                                    <div className="form-label">
                                        {t("organization_settings.time_format_field_label")}
                                    </div>
                                    <DropDownList
                                        id="timeFormatDropdown"
                                        data={timeFormatOptions}
                                        textField="label"
                                        valueField="value"
                                        value={timeFormat}
                                        onChange={(e) => setTimeFormat(e.target.value)}
                                    />
                                </div>
                                
                                <div className="mt-4">
                                    <div className="form-label">
                                        {t("organization_settings.start_of_week_field_label")}
                                    </div>
                                    <DropDownList
                                        id="startOfWeekDropdown"
                                        data={startOfWeekOptions}
                                        textField="label"
                                        valueField="value"
                                        value={startOfWeek}
                                        onChange={(e) => setStartOfWeek(e.target.value)}
                                    />
                                </div>
                                
                                <div className="mt-4">
                                    <div className="form-label">
                                        {t("organization_settings.use_fiscal_calendar_field_label")}
                                    </div>
                                    <Switch
                                        name="useFiscalCalendar"
                                        onChange={(e) => setUseFiscalCalendar(e.target.value)}
                                        checked={useFiscalCalendar}
                                    />
                                </div>
                                
                                {useFiscalCalendar && (
                                    <>
                                        <div className="mt-4">
                                            <div className="form-label">
                                                {t("organization_settings.fiscal_year_start_month_field_label")}
                                            </div>
                                            <DropDownList
                                                id="fiscalYearStartMonthDropdown"
                                                data={fiscalYearStartMonthOptions}
                                                textField="label"
                                                valueField="value"
                                                value={fiscalYearStartMonth}
                                                onChange={(e) => setFiscalYearStartMonth(e.target.value)}
                                            />
                                            <div className="text-muted mt-1">
                                                {t("organization_settings.fiscal_year_end_month_label")}: {fiscalYearStartMonth === "January" ? "December" : 
                                                    fiscalYearStartMonth === "February" ? "January" :
                                                    fiscalYearStartMonth === "March" ? "February" :
                                                    fiscalYearStartMonth === "April" ? "March" :
                                                    fiscalYearStartMonth === "May" ? "April" :
                                                    fiscalYearStartMonth === "June" ? "May" :
                                                    fiscalYearStartMonth === "July" ? "June" :
                                                    fiscalYearStartMonth === "August" ? "July" :
                                                    fiscalYearStartMonth === "September" ? "August" :
                                                    fiscalYearStartMonth === "October" ? "September" :
                                                    fiscalYearStartMonth === "November" ? "October" :
                                                    fiscalYearStartMonth === "December" ? "November" : "December"}
                                            </div>
                                        </div>
                                        
                                        <div className="mt-4">
                                            <div className="form-label">
                                                {t("organization_settings.fiscal_year_label_format_field_label")}
                                            </div>
                                            <DropDownList
                                                id="fiscalYearLabelFormatDropdown"
                                                data={fiscalYearLabelFormatOptions}
                                                textField="label"
                                                valueField="value"
                                                value={fiscalYearLabelFormat}
                                                onChange={(e) => setFiscalYearLabelFormat(e.target.value)}
                                            />
                                        </div>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </Scrollable>
            </Content>
        </PageContainer>
    );
}

export default Overview;
